import { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import OutlinedInput from "@mui/material/OutlinedInput"
import Checkbox from "@mui/material/Checkbox"
import ListItemText from "@mui/material/ListItemText"
import CircularProgress from "@mui/material/CircularProgress"
import Divider from "@mui/material/Divider"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"

import { fetchAllServicios, fetchColumnas, updateColumnas } from "./service"
import { showToast } from "utils/swalToast"

const getColClave = (col) => col.clave ?? col.key ?? col.nombre ?? String(col.id ?? "")
const getColEtiqueta = (col) => col.etiqueta ?? col.label ?? col.nombre ?? getColClave(col)

function AddColumnasVista({ id, onSaved }) {
  const [servicios, setServicios] = useState([])
  // { [idServicio]: columnas[] }
  const [columnasMap, setColumnasMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id) return
    setLoading(true)
    fetchAllServicios()
      .then(async (list) => {
        setServicios(list)
        const map = {}
        for (const s of list) {
          try {
            const cols = await fetchColumnas(id, s.name)
            map[s.idServicio] = Array.isArray(cols) ? cols : []
          } catch {
            map[s.idServicio] = []
          }
        }
        setColumnasMap(map)
      })
      .catch(() => {
        setServicios([])
        setColumnasMap({})
      })
      .finally(() => setLoading(false))
  }, [id])

  const handleChangeVisible = (idServicio, selectedKeys) => {
    setColumnasMap((prev) => ({
      ...prev,
      [idServicio]: (prev[idServicio] || []).map((col) => ({
        ...col,
        visible: selectedKeys.includes(getColClave(col)),
      })),
    }))
  }

  const handleGuardar = async () => {
    try {
      setSaving(true)
      const allColumnas = Object.values(columnasMap).flat().map((col) => ({
        ...col,
        idColumna: col.idColumna ?? col.id,
      }))
      await updateColumnas(id, allColumnas)
      showToast({ title: "Configuración de columnas guardada", icon: "success" })
      onSaved()
    } catch (error) {
      console.error(error)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <MDBox p={3} display="flex" justifyContent="center">
        <CircularProgress size={32} />
      </MDBox>
    )
  }

  if (servicios.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="body2" color="text">
          Esta sucursal no tiene servicios asignados.
        </MDTypography>
      </MDBox>
    )
  }

  return (
    <MDBox p={2}>
      <MDTypography variant="h6" mb={2}>
        Configuración de columnas por servicio
      </MDTypography>

      {servicios.map((s, idx) => {
        const columnas = columnasMap[s.idServicio] || []
        const visiblesKeys = columnas.filter((c) => c.visible).map(getColClave)

        return (
          <MDBox key={s.idServicio}>
            {idx > 0 && <Divider sx={{ my: 1 }} />}
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid item xs={12} sm={4}>
                <MDTypography variant="button" fontWeight="medium" color="text">
                  {s.name}
                </MDTypography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormControl fullWidth>
                  <InputLabel>Columnas a mostrar</InputLabel>
                  <Select
                    multiple
                    label="Columnas a mostrar"
                    value={visiblesKeys}
                    onChange={(e) => handleChangeVisible(s.idServicio, e.target.value)}
                    input={<OutlinedInput label="Columnas a mostrar" />}
                    renderValue={(selected) =>
                      selected.length === 0
                        ? "Ninguna"
                        : selected
                            .map((k) => {
                              const col = columnas.find((c) => getColClave(c) === k)
                              return col ? getColEtiqueta(col) : k
                            })
                            .join(", ")
                    }
                    sx={{ p: 1.5 }}
                  >
                    {columnas.length === 0 ? (
                      <MenuItem disabled>
                        <em>Sin columnas disponibles</em>
                      </MenuItem>
                    ) : (
                      columnas.map((col) => {
                        const clave = getColClave(col)
                        return (
                          <MenuItem key={clave} value={clave}>
                            <Checkbox checked={col.visible ?? false} />
                            <ListItemText primary={getColEtiqueta(col)} />
                          </MenuItem>
                        )
                      })
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </MDBox>
        )
      })}

      <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
        <MDButton variant="outlined" color="info" onClick={onSaved} disabled={saving}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="success"
          onClick={handleGuardar}
          disabled={saving}
        >
          {saving ? "Guardando..." : "Guardar"}
        </MDButton>
      </MDBox>
    </MDBox>
  )
}

export default AddColumnasVista
